import { useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { PatchTrakt, TraktLink, TraktPending, TraktState } from "@rago/web-shared";
import { WATCH_STATE_SYNC_KEY, nudgeServerGrants, saveWatchStateSync } from "../../../lib/profile";
import {
  INTERVAL_OPTIONS,
  TRAKT_KEY,
  accountLine,
  describeTraktError,
  disconnectTrakt,
  draftPatch,
  draftedOptions,
  importedLine,
  intervalChoice,
  intervalLine,
  intervalPatch,
  isSyncing,
  patchTrakt,
  requestTraktSync,
  sentLine,
  syncsAnything,
  useTrakt,
} from "../../../lib/trakt";
import { ConfirmDialog } from "../../../components/modal";
import {
  SheetHeader,
  SheetLoading,
  SheetNotice,
  SheetSection,
  StateChip,
  UnsavedBar,
  type StateChipTone,
} from "../../../components/settings-sheet";
import { TraktConnect } from "../../../components/trakt/connect-panel";
import { InitialBadge, SegmentedChoice, Spinner, Switch } from "../../../components/ui";

export const Route = createFileRoute("/_authed/settings/trakt")({
  component: TraktSettings,
});

/** The interval radios' shared name. */
const INTERVAL_NAME = "traktInterval";
/** The line under the account's name; it is also why Sync now rests with nothing switched on. */
const ACCOUNT_LINE_ID = "traktAccountLine";

const LEDE =
  "Keep Trakt and Rago in step: what you watch on your Rago servers goes to your Trakt history, and what Trakt has comes here and is marked watched on your servers.";

/** Connecting, as the page's sections drive it (components/trakt/connect-panel.tsx). */
interface ConnectControl {
  /** The panel is showing in place of the button. */
  open: boolean;
  /** …because the button was pressed just now (it takes the focus), not
   * because a code was still waiting from an earlier visit. */
  byClick: boolean;
  pending: TraktPending | null;
  /** The panel reports whether a code is out; the header's chip says so. */
  setWaiting: (waiting: boolean) => void;
  start: () => void;
  cancel: () => void;
  connected: (state: TraktState) => void;
}

/**
 * Settings › Trakt: connect a Trakt account, choose which way plays travel,
 * see where the sync stands. The syncing is the broker's (it runs with no
 * page open). The options — the two directions and the interval — are
 * edited on the page and saved together from the floating "Unsaved
 * changes" pill (settings-sheet UnsavedBar; Kenny's design, first here:
 * until it, each applied the moment it was changed). Sync now and
 * Disconnect act at once, on what is saved. A new connection starts with
 * both directions off and syncs nothing (Kenny): Sync now rests until one
 * is switched on and saved, and the account's line says why.
 * Connecting happens in the page too: the page gives way to the code and
 * the way to Trakt, and becomes the connected one when Trakt agrees
 * (Kenny: no dialog for it).
 *
 * The look is Kenny's (Claude Design, 2026-10), made to fill the sheet: the
 * state as a chip beside the title, then one of three bodies — what the
 * connection does, as two cards over the button; the code in the middle of
 * the sheet; the account as a card over the rows of what syncs, each with
 * its switch, and how often — the sync interval, one for both directions,
 * the account's to choose (hourly to weekly, or Manual: only Sync now).
 * Cards, switches and the segmented choice are this page's: the other
 * pages keep to hairlines and checkboxes.
 */
function TraktSettings() {
  const qc = useQueryClient();
  const trakt = useTrakt();
  const state = trakt.data;
  const adopt = (next: TraktState) => qc.setQueryData(TRAKT_KEY, next);
  // null = not asked either way yet: a code still waiting from an earlier
  // visit opens the panel by itself. After a Cancel it stays closed.
  const [connecting, setConnecting] = useState<boolean | null>(null);
  const [waiting, setWaiting] = useState(true);
  const connect: ConnectControl = {
    open: connecting ?? Boolean(state?.pending),
    byClick: connecting === true,
    pending: state?.pending ?? null,
    setWaiting,
    start: () => {
      setWaiting(true);
      setConnecting(true);
    },
    cancel: () => {
      setConnecting(false);
      if (state) adopt({ ...state, pending: null });
    },
    connected: (next) => {
      setConnecting(false);
      adopt(next);
    },
  };

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill">
      <SheetHeader
        title="Trakt"
        lede={LEDE}
        status={state?.available ? <StateChip {...chipOf(state, connect.open && waiting)} /> : null}
      />
      {!state ? (
        trakt.isError ? (
          <SheetSection>
            <p role="alert" className="text-sm text-[var(--rago-error)]">
              {describeTraktError(trakt.error)}
            </p>
          </SheetSection>
        ) : (
          <SheetLoading />
        )
      ) : !state.available ? (
        <SheetNotice>Trakt can't be connected right now. Try again later.</SheetNotice>
      ) : state.link ? (
        <Connected state={state} link={state.link} adopt={adopt} connect={connect} />
      ) : (
        <NotConnected state={state} connect={connect} />
      )}
    </div>
  );
}

/** Where the connection stands, in two words, beside the title. */
function chipOf(state: TraktState, waiting: boolean): { tone: StateChipTone; label: string } {
  if (state.link) {
    if (state.link.status === "reauth_required") return { tone: "warn", label: "Reconnect needed" };
    if (!state.watchStateSync) return { tone: "muted", label: "Paused" };
    return { tone: "ok", label: "Connected" };
  }
  return waiting && state.watchStateSync
    ? { tone: "accent", label: "Waiting for Trakt" }
    : { tone: "muted", label: "Not connected" };
}

/** "RAGO → TRAKT": which way a thing travels. `boxed` is the tag beside a row's title. */
function Direction(props: { from: string; to: string; boxed?: boolean }) {
  return (
    <span
      className={
        props.boxed
          ? "rounded-[6px] bg-white/[0.07] px-1.5 py-[3px] text-[10px] leading-none font-medium tracking-[0.12em] whitespace-nowrap text-[var(--rago-text-3)] uppercase"
          : "rago-caps whitespace-nowrap"
      }
    >
      {props.from} <span aria-hidden>→</span>
      <span className="sr-only">to</span> {props.to}
    </span>
  );
}

function NotConnected(props: { state: TraktState; connect: ConnectControl }) {
  const { connect } = props;
  const ready = props.state.watchStateSync;
  const open = connect.open && ready;
  // Cancel brings the button back: it gets the focus the panel had.
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) buttonRef.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  if (open) {
    return (
      <SheetSection>
        <div className="py-2">
          <TraktConnect
            pending={connect.pending}
            takeFocus={connect.byClick}
            onState={connect.connected}
            onCancel={connect.cancel}
            onWaiting={connect.setWaiting}
          />
        </div>
      </SheetSection>
    );
  }
  return (
    <SheetSection>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,17rem),1fr))] gap-3.5 pt-2">
        <DirectionCard
          from="Rago"
          to="Trakt"
          title="Scrobble what you watch"
          body="Movies and episodes you finish on your servers are added to your Trakt history."
        />
        <DirectionCard
          from="Trakt"
          to="Rago"
          title="Bring your history home"
          body="Your Trakt history is marked watched in your Rago libraries."
        />
      </div>
      {ready ? null : <ConsentOff id="trakt-consent" lead="Sync my watch state is off, and Trakt needs it." />}
      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
        <button
          ref={buttonRef}
          type="button"
          className="rago-btn"
          aria-disabled={ready ? undefined : true}
          aria-describedby={ready ? undefined : "trakt-consent"}
          onClick={() => {
            if (ready) connect.start();
          }}
        >
          Connect Trakt
        </button>
        <p className="text-sm text-[var(--rago-text-3)]">Rago never sees your Trakt password.</p>
      </div>
    </SheetSection>
  );
}

/** One of the two things the connection does, before it is made. */
function DirectionCard(props: { from: string; to: string; title: string; body: string }) {
  return (
    <div className="rounded-[var(--rago-radius-l)] border border-[var(--rago-line-1)] bg-white/[0.03] px-5 pt-5 pb-6">
      <Direction from={props.from} to={props.to} />
      <p className="mt-2.5 text-[17px] font-semibold text-[var(--rago-text-1)]">{props.title}</p>
      <p className="mt-2 text-sm leading-relaxed text-[var(--rago-text-3)]">{props.body}</p>
    </div>
  );
}

/**
 * "Sync my watch state" is off (Settings › Account): Trakt's plays move
 * through the account's watch state, under the same consent as every
 * server's. The way to turn it on is right here.
 */
function ConsentOff(props: { id?: string; lead: string }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function turnOn() {
    setSaving(true);
    setError(null);
    try {
      qc.setQueryData(WATCH_STATE_SYNC_KEY, await saveWatchStateSync(true));
      nudgeServerGrants();
      await qc.invalidateQueries({ queryKey: TRAKT_KEY });
    } catch (err) {
      setError(describeTraktError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-[var(--rago-radius-m)] border border-[var(--rago-line-2)] px-4 py-3">
      <p id={props.id} className="min-w-0 text-sm text-[var(--rago-text-2)]">
        <span className="text-[var(--rago-text-1)]">{props.lead}</span> It carries what you've
        watched between Trakt, Rago and your servers.
        {error ? (
          <span role="alert" className="mt-1 block text-xs text-[var(--rago-error)]">
            {error}
          </span>
        ) : null}
      </p>
      <button
        type="button"
        className="rago-btn rago-btn-quiet rago-btn-sm shrink-0"
        aria-disabled={saving ? true : undefined}
        onClick={() => {
          if (!saving) void turnOn();
        }}
      >
        {saving ? <Spinner /> : null}
        Turn on
      </button>
    </div>
  );
}

function Connected(props: {
  state: TraktState;
  link: TraktLink;
  adopt: (state: TraktState) => void;
  connect: ConnectControl;
}) {
  const qc = useQueryClient();
  const { link, state, connect } = props;
  const refused = link.status === "reauth_required";
  // Trakt refused the stored connection: connecting again happens here,
  // under the account it concerns.
  const reconnecting = refused && connect.open;

  // The options are edited as a draft over the saved link: the controls
  // show link + draft, and the pill is there while the two differ. The draft
  // holds only what was touched, so the page's refetch never undoes an edit.
  const [draft, setDraft] = useState<PatchTrakt>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const options = draftedOptions(link, draft);
  const patch = draftPatch(link, draft);
  const dirty = Object.keys(patch).length > 0;
  // The control changed last: it gets the focus back when the pill leaves.
  const lastTouched = useRef<string | null>(null);
  function edit(change: PatchTrakt, control: string) {
    lastTouched.current = control;
    setDraft((d) => ({ ...d, ...change }));
    setSaved(false);
    setSaveError(null);
  }
  function revert() {
    setDraft({});
    setSaveError(null);
  }
  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      props.adopt(await patchTrakt(patch));
      setDraft({});
      setSaved(true);
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }
  function focusLastTouched() {
    const id = lastTouched.current;
    const control =
      id === INTERVAL_NAME
        ? document.querySelector<HTMLElement>(`input[name="${INTERVAL_NAME}"]:checked`)
        : id
          ? document.getElementById(id)
          : null;
    control?.focus({ preventScroll: true });
  }

  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<unknown>(null);
  async function disconnect() {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await disconnectTrakt();
      setDisconnectOpen(false);
      await qc.invalidateQueries({ queryKey: TRAKT_KEY });
    } catch (err) {
      setDisconnectError(err);
    } finally {
      setDisconnecting(false);
    }
  }

  const name = link.username || "Trakt account";
  const line = accountLine(link);

  return (
    <>
      <SheetSection>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-4 rounded-[var(--rago-radius-l)] border border-[var(--rago-line-1)] bg-white/[0.03] py-4 pr-4 pl-5">
          <div className="flex min-w-0 items-center gap-4">
            <InitialBadge name={name} size="md" />
            <div className="min-w-0">
              <p className="text-[17px] font-medium text-[var(--rago-text-1)]">
                {link.profileUrl ? (
                  <a
                    href={link.profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="[overflow-wrap:anywhere] underline-offset-4 hover:underline"
                  >
                    {name}
                  </a>
                ) : (
                  <span className="[overflow-wrap:anywhere]">{name}</span>
                )}
              </p>
              <p
                id={ACCOUNT_LINE_ID}
                role="status"
                className={`mt-0.5 flex items-center gap-1.5 text-sm ${
                  line.tone === "warn" ? "text-[var(--rago-warn)]" : "text-[var(--rago-text-3)]"
                }`}
              >
                {line.tone === "busy" && state.watchStateSync ? (
                  <span className="shrink-0 text-[var(--rago-accent)]">
                    <Spinner size={14} />
                  </span>
                ) : null}
                {line.text}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            {refused ? (
              reconnecting ? null : (
                <button type="button" className="rago-btn rago-btn-sm" onClick={connect.start}>
                  Connect again
                </button>
              )
            ) : (
              <SyncNowButton link={link} disabled={!state.watchStateSync} adopt={props.adopt} />
            )}
            <button
              type="button"
              className="rago-btn rago-btn-danger rago-btn-sm"
              aria-haspopup="dialog"
              onClick={(e) => {
                e.currentTarget.focus();
                setDisconnectError(null);
                setDisconnectOpen(true);
              }}
            >
              Disconnect
            </button>
          </div>
        </div>
        {/* Trakt refused the stored connection: connecting again happens
            here, under the account it concerns. */}
        {reconnecting ? (
          <div className="mt-6">
            <TraktConnect
              pending={connect.pending}
              takeFocus={connect.byClick}
              onState={connect.connected}
              onCancel={connect.cancel}
            />
          </div>
        ) : null}
        {state.watchStateSync ? null : (
          <ConsentOff lead="Sync my watch state is off, so nothing is syncing." />
        )}

        <h2 className="rago-caps mt-8">What syncs</h2>
        <ul className="mt-3 divide-y divide-[var(--rago-line-1)] border-y border-[var(--rago-line-1)]">
          <SyncRow
            id="traktExport"
            title="Scrobble playback"
            direction={<Direction from="Rago" to="Trakt" boxed />}
            hint="Finished movies and episodes are added to your Trakt history."
            count={sentLine(link.totals)}
            checked={options.exportEnabled}
            disabled={saving}
            onChange={(checked) => edit({ exportEnabled: checked }, "traktExport")}
          />
          <SyncRow
            id="traktImport"
            title="Import watch history"
            direction={<Direction from="Trakt" to="Rago" boxed />}
            hint="Items in your Trakt history are marked watched on your servers."
            count={importedLine(link.totals)}
            checked={options.importEnabled}
            disabled={saving}
            onChange={(checked) => edit({ importEnabled: checked }, "traktImport")}
          />
        </ul>
        <p className="mt-4 text-xs text-[var(--rago-text-3)]">
          Marking something unwatched stays where you do it: nothing is ever removed on the other
          side. Disconnecting keeps what was imported.
        </p>

        <h2 className="rago-caps mt-8">Sync interval</h2>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3.5 border-t border-[var(--rago-line-1)] pt-4">
          <div className="min-w-0">
            <p id="traktIntervalTitle" className="text-[15px] font-medium text-[var(--rago-text-1)]">
              Sync with Trakt
            </p>
            <p id="traktIntervalLine" className="mt-1 text-sm text-[var(--rago-text-3)]">
              {intervalLine(options)}
            </p>
          </div>
          <div className="max-[640px]:w-full">
            <SegmentedChoice
              name={INTERVAL_NAME}
              labelledBy="traktIntervalTitle"
              describedBy="traktIntervalLine"
              value={intervalChoice(options.syncIntervalS)}
              options={INTERVAL_OPTIONS}
              busy={saving}
              onChange={(choice) => edit(intervalPatch(choice), INTERVAL_NAME)}
            />
          </div>
        </div>
      </SheetSection>

      <UnsavedBar
        dirty={dirty}
        saving={saving}
        saved={saved}
        error={saveError ? describeTraktError(saveError) : null}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={focusLastTouched}
      />

      <ConfirmDialog
        open={disconnectOpen}
        title="Disconnect Trakt?"
        body={
          <>
            Rago stops syncing with{" "}
            <span className="[overflow-wrap:anywhere] text-[var(--rago-text-1)]">
              {link.username || "your Trakt account"}
            </span>{" "}
            and forgets the connection. Your Rago watch history keeps everything that was
            imported, and your Trakt history is left as it is.
          </>
        }
        confirmLabel="Disconnect"
        danger
        busy={disconnecting}
        error={disconnectError}
        onConfirm={() => void disconnect()}
        onCancel={() => {
          if (!disconnecting) setDisconnectOpen(false);
        }}
      />
    </>
  );
}

/** One thing that syncs: what, which way, how much so far — and its switch at the row's edge. */
function SyncRow(props: {
  id: string;
  title: string;
  direction: ReactNode;
  hint: string;
  /** What has moved this way so far; null before anything has. */
  count: string | null;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const hintId = `${props.id}-hint`;
  return (
    <li className="flex items-center justify-between gap-6 py-4">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <label htmlFor={props.id} className="text-[15px] font-medium text-[var(--rago-text-1)]">
            {props.title}
          </label>
          {props.direction}
        </p>
        <p id={hintId} className="mt-1 text-sm text-[var(--rago-text-3)]">
          {props.hint}
        </p>
        {props.count ? (
          <p className="mt-1 text-xs text-[var(--rago-text-dim)]">{props.count}</p>
        ) : null}
      </div>
      <Switch
        id={props.id}
        checked={props.checked}
        disabled={props.disabled}
        onChange={props.onChange}
        describedBy={hintId}
      />
    </li>
  );
}

/**
 * Sync now: asks the broker, then the page's own progress line takes over.
 * With both directions off (as saved) there is nothing to sync and the
 * broker would do nothing, so the button rests.
 */
function SyncNowButton(props: {
  link: TraktLink;
  disabled: boolean;
  adopt: (state: TraktState) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const syncing = isSyncing(props.link);
  const nothingOn = !syncsAnything(props.link);
  const resting = asking || syncing || props.disabled || nothingOn;

  async function run() {
    setAsking(true);
    setError(null);
    try {
      props.adopt(await requestTraktSync());
    } catch (err) {
      setError(describeTraktError(err));
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {error ? (
        <p role="alert" className="text-xs text-[var(--rago-error)]">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        className="rago-btn rago-btn-quiet rago-btn-sm"
        aria-disabled={resting ? true : undefined}
        aria-describedby={nothingOn ? ACCOUNT_LINE_ID : undefined}
        onClick={() => {
          if (!resting) void run();
        }}
      >
        {asking || syncing ? <Spinner size={12} /> : null}
        {syncing ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}
